import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { LuBuilding2, LuUsers, LuCar, LuCalendarCheck, LuMapPin, LuClock, LuRoute, LuUserCheck } from 'react-icons/lu';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

const STATUS_COLORS = {
  scheduled:   { bg: 'bg-hajj-teal/15', text: 'text-teal-700' },
  in_progress: { bg: 'bg-hajj-gold/15', text: 'text-amber-700' },
  completed:   { bg: 'bg-green-50',     text: 'text-green-700' },
  cancelled:   { bg: 'bg-red-50',       text: 'text-red-600' },
};
const STATUS_LABELS = { scheduled: 'Scheduled', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };

const FILTERS = [
  { key: 'today',     label: 'Today' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month',label: 'This Month' },
  { key: 'this_year', label: 'This Year' },
];

const getDateRange = (filter) => {
  const now = new Date();
  switch (filter) {
    case 'today':
      return { from: format(now, 'yyyy-MM-dd'), to: format(now, 'yyyy-MM-dd') };
    case 'this_week':
      return { from: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'), to: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
    case 'this_month':
      return { from: format(startOfMonth(now), 'yyyy-MM-dd'), to: format(endOfMonth(now), 'yyyy-MM-dd') };
    case 'this_year':
      return { from: format(startOfYear(now), 'yyyy-MM-dd'), to: format(endOfYear(now), 'yyyy-MM-dd') };
    default:
      return { from: format(now, 'yyyy-MM-dd'), to: format(now, 'yyyy-MM-dd') };
  }
};

function StatCard({ icon: Icon, label, value, colorClass, note }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-hajj-sm p-5 flex items-center gap-4 hover:shadow-hajj-md hover:-translate-y-0.5 transition-all duration-200">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${colorClass}`}>
        <Icon />
      </div>
      <div>
        <div className="text-2xl font-extrabold text-hajj-dark leading-none">{value ?? '—'}</div>
        <div className="text-xs text-gray-400 font-medium mt-1">{label}</div>
        {note && <div className="text-[10px] text-gray-300 mt-0.5 italic">{note}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [filter, setFilter] = useState('today');
  const [stats, setStats] = useState({});
  const [recentTrips, setRecentTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [recentLoading, setRecentLoading] = useState(true);

  // Load entity counts once (not affected by date filter)
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        if (isAdmin) {
          const [cR, eR, dR, rR, odR] = await Promise.all([
            api.get('/companies'), api.get('/employees'), api.get('/drivers'),
            api.get('/routes'),
            api.get('/drivers?dutyStatus=on_duty&limit=1'),
          ]);
          setStats(s => ({ ...s, companies: cR.data.data?.length, employees: eR.data.data?.length, drivers: dR.data.data?.length, routes: rR.data.data?.length, onDutyDrivers: odR.data.pagination?.total ?? 0 }));
        } else {
          const [eR, dR, odR] = await Promise.all([
            api.get('/employees'), api.get('/drivers'),
            api.get('/drivers?dutyStatus=on_duty&limit=1'),
          ]);
          setStats(s => ({ ...s, employees: eR.data.data?.length, drivers: dR.data.data?.length, onDutyDrivers: odR.data.pagination?.total ?? 0 }));
        }
      } catch (e) { console.error(e); }
    };
    fetchCounts();
  }, [isAdmin]);

  // Load stat counts when filter changes (not tied to recent trips)
  const fetchTrips = useCallback(async () => {
    setTripsLoading(true);
    try {
      const { from, to } = getDateRange(filter);
      const { data } = await api.get(`/trips?fromDate=${from}&toDate=${to}`);
      const trips = data.data || [];
      setStats(s => ({
        ...s,
        periodTrips: trips.length,
        upcoming:    trips.filter(t => t.status === 'scheduled').length,
        completed:   trips.filter(t => t.status === 'completed').length,
      }));
    } catch (e) { console.error(e); }
    finally { setTripsLoading(false); }
  }, [filter]);

  // Load last 10 trips once — never affected by date filter
  useEffect(() => {
    const fetchRecent = async () => {
      setRecentLoading(true);
      try {
        const { data } = await api.get('/trips?limit=10');
        setRecentTrips(data.data || []);
      } catch (e) { console.error(e); }
      finally { setRecentLoading(false); }
    };
    fetchRecent();
  }, []);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  const filterLabel = FILTERS.find(f => f.key === filter)?.label || 'Today';

  const statsConfig = [
    ...(isAdmin ? [
      { icon: LuBuilding2,     label: 'Hajj Companies',  value: stats.companies, colorClass: 'bg-hajj-gold/15 text-amber-700' },
      { icon: LuRoute,         label: 'Routes',     value: stats.routes,    colorClass: 'bg-indigo-50 text-indigo-700' },
    ] : []),
    { icon: LuUsers,         label: 'Hajj Agents',            value: stats.employees,     colorClass: 'bg-hajj-teal/15 text-teal-700' },
    { icon: LuCar,           label: 'Drivers',                value: stats.drivers,       colorClass: 'bg-hajj-mid/15 text-hajj-mid' },
    { icon: LuUserCheck,     label: 'On Duty Drivers',        value: stats.onDutyDrivers, colorClass: 'bg-green-50 text-green-700', note: 'Not affected by filter' },
    { icon: LuMapPin,        label: `${filterLabel}'s Trips`, value: stats.periodTrips,colorClass: 'bg-hajj-gold/15 text-amber-700' },
    { icon: LuCalendarCheck, label: 'Scheduled',              value: stats.upcoming,   colorClass: 'bg-hajj-teal/15 text-teal-700' },
    { icon: LuClock,         label: 'Completed',              value: stats.completed,  colorClass: 'bg-green-50 text-green-700' },
  ];

  return (
    <div className="flex flex-col gap-7">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-hajj-dark">Dashboard</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Welcome back, <span className="font-semibold text-hajj-dark">{user?.name}</span> — {format(new Date(), 'EEEE, d MMMM yyyy')}
          </p>
        </div>

        {/* Period filter */}
        <div className="flex bg-white border border-gray-100 rounded-xl p-1 gap-1 shadow-hajj-sm overflow-x-auto">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                filter === f.key
                  ? 'bg-hajj-dark text-white shadow-hajj-sm'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {statsConfig.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Recent trips — always last 10, unaffected by date filter */}
      <div className="card">
        <div className="px-6 py-4 border-b border-[#e2ddd5] flex items-center justify-between">
          <h2 className="text-sm font-bold text-hajj-dark">Recent Trips</h2>
          {recentLoading && <div className="w-4 h-4 border-2 border-gray-200 border-t-hajj-gold rounded-full animate-spin" />}
        </div>

        {recentLoading ? (
          <div className="flex justify-center py-14">
            <div className="w-9 h-9 border-[3px] border-gray-200 border-t-hajj-gold rounded-full animate-spin" />
          </div>
        ) : recentTrips.length === 0 ? (
          <div className="text-center py-14 text-gray-400 italic text-sm">No trips found</div>
        ) : (
          <div className="divide-y divide-[#e2ddd5]">
            {recentTrips.map(trip => {
              const sc = STATUS_COLORS[trip.status] || STATUS_COLORS.scheduled;
              return (
                <div key={trip._id} className="flex items-center justify-between px-6 py-3.5 hover:bg-hajj-light/50 transition-colors gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 font-semibold text-hajj-dark text-sm">
                      <span className="truncate">{trip.from}</span>
                      <span className="text-hajj-gold text-xs flex-shrink-0">→</span>
                      <span className="truncate">{trip.to}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5 flex-wrap">
                      <span>{format(new Date(trip.tripDate), 'dd MMM yyyy')}</span>
                      <span>·</span>
                      <span>{trip.departureTime}</span>
                      {trip.driver && <><span>·</span><span>{trip.driver.name}</span></>}
                      {trip.company && <><span>·</span><span>{trip.company.name}</span></>}
                    </div>
                  </div>
                  <span className={`badge ${sc.bg} ${sc.text} whitespace-nowrap`}>
                    {STATUS_LABELS[trip.status]}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
